import type { CircleAiConversation } from "@circle-react/api/circleAiConversationsApi";
import { circleAiProjectQueries } from "@circle-react/lib/queries/circleAiProjects";
import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useInfiniteQuery } from "react-query";

import {
  conversationActionsCopy,
  conversationDeleteDescription,
} from "../conversationActionsCopy";
import { DeleteConversationModal } from "./DeleteConversationModal";
import { RenameConversationModal } from "./RenameConversationModal";
import {
  type ConversationProjectOption,
  useAddConversationToProject,
} from "./useAddConversationToProject";
import { useDeleteConversation } from "./useDeleteConversation";
import { useRenameConversation } from "./useRenameConversation";

type ActionableConversation = Pick<CircleAiConversation, "uuid" | "title">;

export type ProjectsState = "loading" | "error" | "empty" | "ready";

interface ConversationActionsContextValue {
  addToProject: (
    uuid: string,
    project: ConversationProjectOption,
    isMove?: boolean,
  ) => void;
  copy: ReturnType<typeof conversationActionsCopy>;
  onMenuOpenChange: (open: boolean) => void;
  projects: ConversationProjectOption[];
  projectsState: ProjectsState;
  removeFromProject: (uuid: string) => void;
  requestDelete: (conversation: ActionableConversation) => void;
  requestRename: (conversation: ActionableConversation) => void;
}

const ConversationActionsContext =
  createContext<ConversationActionsContextValue | null>(null);
ConversationActionsContext.displayName = "ConversationActionsContext";

export const useConversationActions = (): ConversationActionsContextValue => {
  const context = useContext(ConversationActionsContext);
  if (!context) {
    throw new Error(
      "useConversationActions must be used within a ConversationActionsProvider",
    );
  }
  return context;
};

export const ConversationActionsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const copy = useMemo(() => conversationActionsCopy(), []);
  const [hasOpenedMenu, setHasOpenedMenu] = useState(false);

  const {
    conversationPendingDeletion,
    isDeleting,
    requestDelete,
    cancelDelete,
    confirmDelete,
  } = useDeleteConversation({
    successMessage: copy.delete.successMessage,
    errorMessage: copy.delete.errorMessage,
  });
  const {
    conversationPendingRename,
    isRenaming,
    requestRename,
    cancelRename,
    confirmRename,
  } = useRenameConversation({
    successMessage: copy.rename.successMessage,
    errorMessage: copy.rename.errorMessage,
  });
  const {
    addToProject: addToProjectMutate,
    removeFromProject: removeFromProjectMutate,
    isUpdatingProject,
  } = useAddConversationToProject({
    buildMovedMessage: copy.addToProject.buildMovedMessage,
    buildSuccessMessage: copy.addToProject.buildSuccessMessage,
    errorMessage: copy.addToProject.errorMessage,
    removeSuccessMessage: copy.addToProject.removeSuccessMessage,
    removeErrorMessage: copy.addToProject.removeErrorMessage,
  });

  const isQueryEnabled = hasOpenedMenu;
  const { data, isPending, isError, isFetching, hasNextPage, fetchNextPage } =
    useInfiniteQuery({
      ...circleAiProjectQueries.list(),
      enabled: isQueryEnabled,
    });

  useEffect(() => {
    if (isQueryEnabled && hasNextPage && !isFetching && !isError) {
      void fetchNextPage({ cancelRefetch: false });
    }
  }, [isQueryEnabled, hasNextPage, isFetching, isError, fetchNextPage]);

  const projects = useMemo<ConversationProjectOption[]>(
    () =>
      (data?.pages.flatMap(page => page.records) ?? []).map(project => ({
        uuid: project.uuid,
        name: project.name,
      })),
    [data],
  );

  let projectsState: ProjectsState;
  if (projects.length > 0) {
    projectsState = "ready";
  } else if (isPending || isFetching) {
    projectsState = "loading";
  } else if (isError) {
    projectsState = "error";
  } else {
    projectsState = "empty";
  }

  const onMenuOpenChange = (open: boolean) => {
    if (open) {
      setHasOpenedMenu(true);
    }
  };

  const addToProject = (
    uuid: string,
    project: ConversationProjectOption,
    isMove = false,
  ) => {
    if (!isUpdatingProject) {
      addToProjectMutate(uuid, project, isMove);
    }
  };

  const removeFromProject = (uuid: string) => {
    if (!isUpdatingProject) {
      removeFromProjectMutate(uuid);
    }
  };

  const value: ConversationActionsContextValue = {
    addToProject,
    copy,
    onMenuOpenChange,
    projects,
    projectsState,
    removeFromProject,
    requestDelete,
    requestRename,
  };

  return (
    <ConversationActionsContext.Provider value={value}>
      {children}
      <DeleteConversationModal
        isOpen={Boolean(conversationPendingDeletion)}
        isDeleting={isDeleting}
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
        title={copy.delete.modalTitle}
        confirmText={copy.delete.confirmText}
        description={conversationDeleteDescription(
          conversationPendingDeletion?.title ?? copy.newConversationTitle,
        )}
      />
      <RenameConversationModal
        isOpen={Boolean(conversationPendingRename)}
        isSaving={isRenaming}
        currentTitle={conversationPendingRename?.title ?? null}
        onCancel={cancelRename}
        onSave={confirmRename}
        title={copy.rename.modalTitle}
        confirmText={copy.rename.confirmText}
        inputLabel={copy.rename.inputLabel}
        placeholder={copy.rename.placeholder}
      />
    </ConversationActionsContext.Provider>
  );
};
